import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild,
    OnDestroy,
    AfterViewInit,
} from "@angular/core";
import { UserDm } from "@/_models";
import {
    AuthenticationService,
    USER_IS_AT_LEAST_ADMIN,
    UserService,
} from "@/_services";
import { MenuSection } from "@/_models/menu-item";
import { isAsapAccount } from "@/_models/account.interface";
import { shareReplay } from "rxjs/operators";
import { async, Observable, Subject } from "rxjs";
import { MenuService } from "@/_services/menu.service";
import { QuickStartGuideComponent } from "@/components/shared/quick-start-guide/quick-start-guide.component";
import UserModel = UserDm.UserModel;
import { OnboardingStatus } from "@/_models/onboarding-status.interface";
import { QuickStartGuideService } from "@/_services/quick-start-guide.service";
import { takeUntil } from "rxjs/operators";
import { OnboardingStateService } from "@/_services/onboarding-state.service";

@Component({
    selector: "app-desktop-menu",
    templateUrl: "./desktop-menu.component.html",
    styleUrls: ["./desktop-menu.component.scss"],
})
export class DesktopMenuComponent implements OnInit, OnDestroy, AfterViewInit {
    showForms = true;
    hasLinkedLogins = false;
    isAsap = false;
    showQuickStartButton = false;
    completedSteps = 0;

    @Output() enterEdit: EventEmitter<void> = new EventEmitter<void>();
    @Input() showAccountLink = false;
    @ViewChild("quickStartGuide") quickStartGuide!: QuickStartGuideComponent;
    @ViewChild("quickStartButton") quickStartButton!: any;

    onboardingStatus$: Observable<OnboardingStatus | null>;
    asapFormId$: Observable<string | null>;
    currentUser$: Observable<UserModel>;
    mainMenu$: Observable<MenuSection>;
    formMenu$: Observable<MenuSection>;

    private destroy$ = new Subject<void>();

    constructor(
        private authenticationService: AuthenticationService,
        private userService: UserService,
        private menuService: MenuService,
        private quickStartGuideService: QuickStartGuideService,
        private onboardingState: OnboardingStateService,
        @Inject(USER_IS_AT_LEAST_ADMIN)
        public isAtLeastAdmin$: Observable<boolean>,
    ) {
        this.hasLinkedLogins = this.authenticationService.hasLinkedLogins();
    }

    ngOnInit() {
        this.currentUser$ = this.userService
            .getCurrentUserAsync()
            .pipe(shareReplay());

        this.currentUser$.subscribe((user) => {
            this.isAsap = isAsapAccount(user.account);
            const dismissed = user?.dismissedBanners || [];
            this.showQuickStartButton = !dismissed.includes("quickStart");
        });
        this.mainMenu$ = this.menuService.createDesktopMenu();
        this.formMenu$ = this.menuService.createFormMenu();

        this.onboardingStatus$ = this.onboardingState.onboardingStatus$;
        this.asapFormId$ = this.onboardingState.asapFormId$;

        this.quickStartGuideService.quickStartState$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const syntheticEvent = {
                    target: this.quickStartButton?.nativeElement,
                    currentTarget: this.quickStartButton?.nativeElement,
                    preventDefault: () => {},
                    stopPropagation: () => {},
                } as Event;
                if (this.quickStartGuide) {
                    this.quickStartGuide.toggle(syntheticEvent);
                }
            });
        this.onboardingStatus$
            .pipe(takeUntil(this.destroy$))
            .subscribe((status) => {
                this.completedSteps = this.getCompletedSteps(status);
            });
    }

    ngAfterViewInit() {
        // Auto-open Quick Start Guide only if it's the first time and onboarding is incomplete
        if (this.quickStartGuide && this.shouldAutoOpenQuickStart()) {
            const syntheticEvent = {
                target: this.quickStartButton?.nativeElement,
                currentTarget: this.quickStartButton?.nativeElement,
                preventDefault: () => {},
                stopPropagation: () => {},
            } as Event;
            this.quickStartGuide.toggle(syntheticEvent);
        }
    }

    private shouldAutoOpenQuickStart(): boolean {
        return this.showQuickStartButton && this.completedSteps < 3;
    }

    toggleCollapse() {
        this.showForms = !this.showForms;
    }

    openQuickStart(event: Event) {
        this.quickStartGuideService.toggle(event);
    }

    getCompletedSteps(status: OnboardingStatus | null): number {
        if (!status) return 0;
        let count = 0;
        if (status?.hasReportRecipients) count++;
        if (status?.hasAddedUsers) count++;
        if (status?.hasGeneratedQrCode) count++;
        return count;
    }

    dismissQuickStart(event: Event) {
        event.stopPropagation();
        this.showQuickStartButton = false;
        this.userService.dismissBanner("quickStart", true).subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
